import {Component, Input} from '@angular/core';

const KPI_SEPARATOR = /[·•‧]/g;

// Les données du chapeau se suivent : on fait tourner les couleurs de KPI du site pour
// les distinguer d'un coup d'œil. Le rouge est écarté, il signale une alerte ailleurs.
const KPI_COLORS = ['kpi-blue', 'kpi-green', 'kpi-orange', 'kpi-purple'];

@Component({
    selector: 'app-ai-result',
    templateUrl: './ai-result.component.html',
    styleUrls: ['./ai-result.component.scss']
})
export class AiResultComponent {

    html = '';
    headingHtml = '';

    @Input() set result(value: string) {
        this.splitHeading(value ? this.formatResult(value) : '');
    }

    // Le panneau de corrections est projeté juste après le vrai titre du rapport, pas sous
    // le titre de la modale. On isole donc le premier h1 du HTML produit par l'IA.
    private splitHeading(html: string): void {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const heading = doc.body.querySelector('h1');
        if (!heading) {
            this.headingHtml = '';
            this.html = html;
            return;
        }
        this.headingHtml = heading.outerHTML;
        heading.remove();
        this.html = doc.body.innerHTML;
    }

    // Le chapeau de l'analyse (paragraphe qui suit le titre) est du texte libre :
    // on le remet en forme sans dépendre du balisage exact renvoyé par l'IA.
    private formatResult(html: string): string {
        const result = this.stripCodeFence(html);
        return this.enhance(this.mergeHeaderParagraphs(result).replace(/(<\/h1>\s*)<p>([\s\S]*?)<\/p>/,
            (match, open, body) => this.isKpiHeader(body)
                ? open + this.formatKpiHeader(body)
                : open + '<p>' + this.formatHeader(body) + '</p>'));
    }

    // Certains modèles entourent malgré tout le HTML par ```html … ```.
    // Ces marqueurs ne font pas partie du rapport et s'afficheraient en bas de page.
    private stripCodeFence(html: string): string {
        return html
            .replace(/^\s*```(?:html)?\s*/i, '')
            .replace(/\s*```\s*$/, '');
    }

    // Le chapeau de l'analyse d'annonce énumère ses données séparées par un point médian
    // (Prix retenu : … · Rendement brut : … · Cash-flow : …) : chacune devient un mini KPI.
    private isKpiHeader(body: string): boolean {
        return (body.match(KPI_SEPARATOR) || []).length >= 3;
    }

    private formatKpiHeader(body: string): string {
        const tiles = body
            .replace(/<\/?(strong|b)>/gi, '')
            .split(KPI_SEPARATOR)
            .map(part => part.trim())
            .filter(part => part !== '')
            .map((part, index) => this.formatKpiTile(part, index))
            .join('');

        return '<div class="ai-kpi">' + tiles + '</div>';
    }

    // Reprend le gabarit des KPI du site (card kpi-<couleur> > kpi-card > card-title + card-value).
    // Libellé et valeur sont en div : la feuille de style du résultat impose une taille aux
    // titres h3 à h6, qui écraserait le libellé du KPI.
    private formatKpiTile(part: string, index: number): string {
        const separator = part.indexOf(':');
        const label = separator > 0 ? part.slice(0, separator).trim() : '';
        const value = separator > 0 ? part.slice(separator + 1).trim() : part;
        const color = KPI_COLORS[index % KPI_COLORS.length];

        return '<div class="card ' + color + '"><div class="card-body kpi-card">'
            + (label !== '' ? '<div class="card-title">' + label + '</div>' : '')
            + '<div class="card-value">' + this.tagKpiValue(label, value) + '</div>'
            + '</div></div>';
    }

    // Une étiquette énergie ou climat garde ses couleurs, comme dans le reste du rapport.
    private tagKpiValue(label: string, value: string): string {
        if (!/DPE|Classe|Étiquette|GES|Climat/i.test(label)) {
            return value;
        }
        const scale = /GES|Climat/i.test(label) ? 'ges' : 'dpe';
        return value.replace(/\b([A-G])\b/g, `<span class="${scale}-tag ${scale}-tag-$1">$1</span>`);
    }

    // Certains rapports coupent le chapeau en deux paragraphes : l'identité du bien d'un côté,
    // les étiquettes de l'autre. On les réunit en un seul, sinon le second échappe à la mise en
    // forme du chapeau et ses étiquettes DPE/GES restent en texte brut. Le second paragraphe
    // n'est repris que s'il porte bien des données d'étiquette, pour ne pas avaler une
    // introduction rédigée.
    private mergeHeaderParagraphs(html: string): string {
        return html.replace(/(<\/h1>\s*<p>)([\s\S]*?)<\/p>\s*<p>([\s\S]*?)(<\/p>)/,
            (match, open, first, second, close) => /Classe|Étiquette|GES|Climat|kWh/i.test(second)
                ? open + first + '<br>' + second + close
                : match);
    }

    // Retouches qui demandent de voir la structure du rapport, en une seule lecture du DOM.
    private enhance(html: string): string {
        const doc = new DOMParser().parseFromString(html, 'text/html');

        this.dropRulesBeforeTitles(doc);
        this.highlightVerdict(doc);
        this.highlightMainPosts(doc);
        this.highlightAlerts(doc);

        return doc.body.innerHTML;
    }

    // L'IA sépare ses sections par un filet ; les titres en portent déjà un. Sans ça,
    // deux traits se suivent au-dessus de chaque section.
    private dropRulesBeforeTitles(doc: Document): void {
        doc.querySelectorAll('hr').forEach(rule => {
            const next = rule.nextElementSibling;
            if (next && next.tagName === 'H2') {
                rule.remove();
            }
        });
    }

    // Le verdict est la conclusion de l'analyse : titre et paragraphes passent dans un encart.
    private highlightVerdict(doc: Document): void {
        const title = Array.from(doc.querySelectorAll('h2'))
            .find(heading => /^verdict\b/i.test(heading.textContent.trim()));
        if (!title) {
            return;
        }

        // Selon le modèle, le texte du verdict peut être un <p> ou du texte brut entre
        // deux titres. On normalise seulement ce second cas pour conserver le regroupement
        // existant des paragraphes dans l'encart verdict.
        this.wrapVerdictPlainText(title, doc);

        const block = doc.createElement('div');
        block.className = 'ai-verdict';
        title.parentNode.insertBefore(block, title);

        let node: Element | null = title;
        while (node && (node === title || node.tagName === 'P')) {
            const next = node.nextElementSibling;
            block.appendChild(node);
            node = next;
        }
    }

    private wrapVerdictPlainText(title: Element, doc: Document): void {
        let node = title.nextSibling;
        // Un verdict peut déjà commencer par un <p>, puis être suivi de texte brut.
        // On traverse donc tous les paragraphes et nœuds texte qui se suivent, jusqu'au
        // prochain élément de structure (en pratique le h2 suivant).
        while (node && (node.nodeType === Node.TEXT_NODE
            || (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'P'))) {
            const next = node.nextSibling;
            if (node.nodeType === Node.TEXT_NODE) {
                const content = (node.textContent || '').trim();
                if (content !== '') {
                    const paragraph = doc.createElement('p');
                    paragraph.textContent = content;
                    title.parentNode.insertBefore(paragraph, node);
                }
                node.parentNode.removeChild(node);
            }
            node = next;
        }
    }

    // Une ligne de tableau conclue par « Alerte » est un point bloquant : elle passe en rouge.
    private highlightAlerts(doc: Document): void {
        doc.querySelectorAll('tbody tr').forEach(row => {
            const cell = Array.from(row.children).find(child => /^alerte\b/i.test(child.textContent.trim()));
            if (cell) {
                row.classList.add('alert-post');
                cell.classList.add('alert-cell');
            }
        });
    }

    // Signale en rouge les lignes de tableau dont la part dépasse 40 % : ce sont les postes
    // sur lesquels tout se joue. La colonne est repérée par son libellé, l'IA la nomme librement.
    private highlightMainPosts(doc: Document): void {
        doc.querySelectorAll('table').forEach(table => {
            const headers = Array.from(table.querySelectorAll('th'));
            const column = headers.findIndex(header => /part|%/i.test(header.textContent));
            if (column < 0) {
                return;
            }

            table.querySelectorAll('tbody tr').forEach(row => {
                const cell = row.children[column];
                if (!cell) {
                    return;
                }
                const part = parseFloat(cell.textContent.replace(',', '.').replace(/[^0-9.]/g, ''));
                if (part > 40) {
                    row.classList.add('main-post');
                }
            });
        });
    }

    // Ligne d'identité gardée telle quelle, puis un libellé par ligne : on ne coupe ni sur une
    // virgule décimale (sans espace) ni devant une valeur chiffrée, qui reste avec son libellé.
    // Le texte vient d'un LLM : chaque format déjà rencontré garde sa règle, en plus de la règle générale.
    private formatHeader(body: string): string {
        const parts = body.split(/<br\s*\/?>/i);
        const lines = parts
            .map((line, index) => {
                const isIdentity = /Adresse/i.test(line) || (index === 0 && parts.length > 1);
                return isIdentity ? line : line
                    .replace(/,\s+(?=\D)/g, '<br>')
                    // Une date de réalisation ou une consommation sont des données à part
                    // entière, pas la valeur du libellé qui précède : la règle générale ne
                    // coupe pas devant un chiffre, ces deux cas s'ajoutent après elle.
                    .replace(/,\s+(?=\d{1,2}\/\d{1,2}\/\d{2,4}\b)/g, '<br>')
                    .replace(/,\s+(?=[\d.,]+\s*kWh)/g, '<br>');
            })
            .join('<br>')
            .replace(/,\s*(?=<strong>)/g, '<br>');

        return lines
            .replace(/(Classe(?:\s+énergie)?|Étiquette(?:\s+énergie|\s+climat)?|DPE|GES|Climat)\s*:?\s*(<\/strong>)?\s*:?\s*([A-G])\b/g,
                (match, label, closing, letter) => {
                    const scale = /ges|climat/i.test(label) ? 'ges' : 'dpe';
                    return `${label}${closing ? closing : ''} : <span class="${scale}-tag ${scale}-tag-${letter}">${letter}</span>`;
                })
            .replace(/(<strong>\s*Classe\s*:\s*<\/strong>)\s*([A-G])\b/g,
                '$1 <span class="dpe-tag dpe-tag-$2">$2</span>')
            .replace(/(<strong>\s*GES\s*:\s*<\/strong>)\s*([A-G])\b/g,
                '$1 <span class="ges-tag ges-tag-$2">$2</span>');
    }
}
